<template>
  <b-modal
    :id="id"
    ref="modal"
    size="xl"
    hide-header
    hide-footer
    body-class="p-0 overflow-hidden"
    content-class="border-0 shadow-lg"
    dialog-class="modal-xl-custom"
    @shown="onShown"
  >
    <!-- ─── HEADER ─── -->
    <div class="ev-report-header">
      <div class="ev-report-header__info">
        <div class="ev-report-header__icon">
          <i class="fa fa-history"></i>
        </div>
        <div>
          <h5 class="ev-report-header__title">Relatório de Embarcados</h5>
          <span class="ev-report-header__sub">Consulta de pedidos finalizados por período</span>
        </div>
      </div>
      <b-button variant="link" class="ev-report-header__close" @click="fechar">
        <i class="fa fa-times"></i>
      </b-button>
    </div>

    <!-- ─── FILTROS ─── -->
    <div class="p-4 bg-light border-bottom">
      <b-row align-v="end" class="gap-3">
        <b-col md="3">
          <label class="modal-label-premium">Data Inicial</label>
          <cec-input-data v-model="filtro.dataInicial" class="shadow-xs" />
        </b-col>
        <b-col md="3">
          <label class="modal-label-premium">Data Final</label>
          <cec-input-data v-model="filtro.dataFinal" class="shadow-xs" />
        </b-col>
        <b-col md="3">
          <b-button variant="primary" pill class="px-4 font-weight-bold shadow-sm" @click="pesquisar" :disabled="carregando">
            <i class="fa fa-search mr-1" :class="{'fa-spin': carregando}"></i> PESQUISAR
          </b-button>
        </b-col>
        <b-col class="text-right">
          <div v-if="pedidos.length" class="text-muted small">
            <strong>{{ pedidos.length }}</strong> pedidos encontrados
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- ─── LISTAGEM ─── -->
    <div class="ev-report-body bg-white overflow-auto" style="max-height: calc(85vh - 200px);">
      <b-overlay :show="carregando" opacity="0.6">
        <b-table
          :items="pedidos"
          :fields="campos"
          hover striped show-empty
          empty-text="Nenhum pedido encontrado para o período informado"
          class="mb-0 ev-report-table"
          thead-class="ev-report-thead"
        >
          <!-- Toggle Detalhes -->
          <template #cell(DETALHE)="row">
            <b-button variant="link" class="p-0 text-secondary" @click.stop="toggleDetalhes(row)">
              <i :class="row.detailsShowing ? 'fa fa-angle-up' : 'fa fa-angle-down'"></i>
            </b-button>
          </template>

          <template #cell(SEQUENCIA)="row">
            <span class="font-weight-bold text-primary">#{{ row.item.SEQUENCIA }}</span>
          </template>

          <template #cell(DT_EXPEDICAO)="row">
            <span class="font-sm">{{ formatarDataHora(row.item.DT_EXPEDICAO) }}</span>
          </template>

          <template #cell(NOME_CLIENTE)="row">
            <div class="font-weight-bold font-sm">{{ row.item.NOME_CLIENTE }}</div>
            <small class="text-muted">{{ row.item.NOME_VENDEDOR || 'Venda Direta' }}</small>
          </template>

          <template #cell(PROGRESSO)="row">
            <div class="d-flex align-items-center gap-2">
              <b-progress :value="calcProgresso(row.item)" :max="100" variant="success" class="flex-grow-1" style="height: 6px;" />
              <small class="font-weight-bold text-success">{{ calcProgresso(row.item) }}%</small>
            </div>
          </template>

          <template #cell(ACOES)="row">
            <b-button
              variant="soft-primary"
              size="sm"
              pill
              v-b-tooltip.hover
              title="Reimprimir Comprovante"
              @click="imprimirComprovante(row.item)"
            >
              <i class="fa fa-print"></i>
            </b-button>
          </template>

          <!-- ROW DETAILS (ITENS DO PEDIDO) -->
          <template #row-details="row">
            <div class="p-4 bg-soft-light border-top border-bottom">
              <div v-if="row.item.loadingItens" class="text-center p-3">
                <b-spinner small variant="primary" /> Carregando itens...
              </div>
              <div v-else>
                <div class="mb-3 d-flex justify-content-between align-items-center">
                  <h6 class="modal-section-title mb-0 font-weight-bold"><i class="fa fa-list mr-2"></i>ITENS DO EMBARQUE</h6>
                  <b-badge variant="soft-dark" pill>{{ (row.item.itens || []).length }} itens</b-badge>
                </div>
                
                <b-table
                  :items="row.item.itens"
                  :fields="camposItens"
                  small hover striped borderless
                  class="bg-white rounded shadow-xs mb-0"
                  thead-class="bg-light font-xs text-muted"
                >
                  <template #cell(QUANT_PEDIDO)="d">
                    <span class="font-weight-bold">{{ formatarQuant(d.item.QUANT_PEDIDO) }}</span>
                  </template>
                  <template #cell(QUANT)="d">
                    <span class="font-weight-bold text-success">{{ formatarQuant(d.item.QUANT) }}</span>
                  </template>
                  <template #cell(PERCENT)="d">
                    <small class="font-weight-bold" :class="calcPctItem(d.item) >= 100 ? 'text-success' : 'text-warning'">
                      {{ calcPctItem(d.item) }}%
                    </small>
                  </template>
                </b-table>
              </div>
            </div>
          </template>
        </b-table>
      </b-overlay>
    </div>

    <!-- ─── FOOTER ─── -->
    <div class="p-3 bg-light border-top text-right">
      <b-button variant="soft-secondary" pill class="px-4 font-weight-bold" @click="fechar">
        FECHAR
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import CecInputData from '@/components/Cec/CecInputData.vue'
import moment from 'moment'
import { imprimirDocumentoCarregamento } from './ExpedicaoVolumesPrint'

export default {
  name: 'ExpedicaoVolumesRelatorioModal',
  components: { CecInputData },
  props: {
    id: { type: String, default: 'modal-relatorio-embarcados' }
  },
  data () {
    return {
      carregando: false,
      filtro: {
        dataInicial: moment().startOf('month').format('YYYY-MM-DD'),
        dataFinal: moment().format('YYYY-MM-DD')
      },
      pedidos: [],
      campos: [
        { key: 'DETALHE', label: '', thStyle: { width: '40px' }, tdClass: 'text-center' },
        { key: 'SEQUENCIA', label: 'Pedido', sortable: true, thClass: 'ev-th' },
        { key: 'DT_EXPEDICAO', label: 'Embarcado Em', sortable: true, thClass: 'ev-th' },
        { key: 'NOME_CLIENTE', label: 'Cliente / Vendedor', sortable: true, thClass: 'ev-th' },
        { key: 'USUARIO_EXPEDICAO', label: 'Usuário', thClass: 'ev-th' },
        { key: 'QUANT_EMBARCADA_TOTAL', label: 'Qtd Total', thClass: 'ev-th text-right', tdClass: 'text-right' },
        { key: 'PROGRESSO', label: 'Status', thClass: 'ev-th' },
        { key: 'ACOES', label: '', thStyle: { width: '60px' }, tdClass: 'text-center' }
      ],
      camposItens: [
        { key: 'ITEM', label: '#' },
        { key: 'COD_PRO', label: 'Cód' },
        { key: 'NOME_PRODUTO', label: 'Produto' },
        { key: 'UNIDADE', label: 'Un' },
        { key: 'QUANT_PEDIDO', label: 'Pedida', class: 'text-right' },
        { key: 'QUANT', label: 'Embarcada', class: 'text-right' },
        { key: 'PERCENT', label: '%', class: 'text-right' }
      ]
    }
  },
  methods: {
    onShown () {
      this.pesquisar()
    },
    fechar () {
      this.$bvModal.hide(this.id)
    },
    async pesquisar () {
      this.carregando = true
      try {
        const response = await this.$http.get('/api/protected/terminal/expedicao/volumes/getPedidosEmbarcadosPeriodo', {
          params: {
            dataInicial: this.filtro.dataInicial,
            dataFinal: this.filtro.dataFinal
          }
        })
        this.pedidos = response.data || []
      } catch (err) {
        this.$bvToast.toast('Erro ao buscar embarcados: ' + (err.response?.data || err.message), {
          title: 'Erro', variant: 'danger'
        })
      } finally {
        this.carregando = false
      }
    },
    async toggleDetalhes (row) {
      if (row.detailsShowing) {
        row.toggleDetails()
        return
      }

      if (!row.item.itens) {
        this.$set(row.item, 'loadingItens', true)
        try {
          const response = await this.$http.get(`/api/protected/terminal/expedicao/volumes/getItensPedido/${row.item.SEQUENCIA}`)
          this.$set(row.item, 'itens', response.data.itens || [])
        } catch (err) {
          this.$bvToast.toast('Erro ao carregar itens: ' + (err.response?.data || err.message), { variant: 'danger' })
        } finally {
          this.$set(row.item, 'loadingItens', false)
        }
      }
      row.toggleDetails()
    },
    calcProgresso (pedido) {
      const p = Number(pedido.QUANT_PEDIDO_TOTAL) || 0
      const e = Number(pedido.QUANT_EMBARCADA_TOTAL) || 0
      if (p === 0) return 0
      return Math.min(100, Math.round((e / p) * 100))
    },
    calcPctItem (item) {
      const p = Number(item.QUANT_PEDIDO) || 0
      const e = Number(item.QUANT) || 0
      if (p === 0) return 0
      return Math.min(100, Math.round((e / p) * 100))
    },
    formatarQuant (v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3 })
    },
    formatarDataHora (v) {
      if (!v) return '-'
      return moment(v).format('DD/MM/YYYY HH:mm')
    },
    async imprimirComprovante (pedido) {
      try {
        await imprimirDocumentoCarregamento(pedido.SEQUENCIA, this.$http)
      } catch (err) {
        this.$bvToast.toast('Erro ao imprimir comprovante', { variant: 'danger' })
      }
    }
  }
}
</script>

<style scoped>
.ev-report-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, #334155 0%, #0f172a 100%);
  color: #fff;
}
.ev-report-header__info { display: flex; align-items: center; gap: 16px; }
.ev-report-header__icon {
  width: 44px; height: 44px;
  background: rgba(255,255,255,0.1);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; color: #38bdf8;
}
.ev-report-header__title { margin: 0; font-weight: 700; font-size: 1.1rem; }
.ev-report-header__sub { font-size: 0.8rem; color: rgba(255,255,255,0.6); }
.ev-report-header__close { color: rgba(255,255,255,0.5); font-size: 1.1rem; padding: 0; }

.ev-report-thead th {
  background: #f8fafc !important; color: #64748b !important;
  font-size: 0.7rem !important; text-transform: uppercase !important;
  letter-spacing: 0.05em !important; padding: 10px 14px !important;
}
.ev-report-table td { vertical-align: middle !important; padding: 10px 14px !important; font-size: 0.85rem; }

.bg-soft-light { background-color: #fcfdfe; }
.bg-soft-dark { background-color: #334155; color: #fff; }
.bg-soft-primary { background-color: #e0f2fe; color: #0369a1; border: none; }
.bg-soft-primary:hover { background-color: #bae6fd; }

.gap-3 { gap: 16px; }
.font-xs { font-size: 0.75rem; }
.font-sm { font-size: 0.85rem; }
</style>



// WEBPACK FOOTER //
// src/views/expedicao/volumes/ExpedicaoVolumesRelatorioModal.vue